import v4api, {getV4AccountUuid, isV4Uuid, v4AuthFor} from '../../api/V4client';
import {fetchStoredV4Whitelabel} from '../v4AccountWhitelabel';

const encodeContent = (value) => {
	const json = JSON.stringify(value);
	const bytes = new TextEncoder().encode(json);
	let binStr = '';
	bytes.forEach(b => binStr += String.fromCharCode(b));
	return btoa(binStr);
};

const decodeContent = (base64) => {
	if (!base64) return null;
	try {
		const binStr = atob(base64);
		const bytes = Uint8Array.from(binStr, c => c.charCodeAt(0));
		const json = new TextDecoder().decode(bytes);
		return JSON.parse(json);
	} catch (e) {
		return null;
	}
};

// When shadowed into a sub-account, the current session token has no access
// to the top-level account — `v4AuthFor` returns the original admin/AM
// Bearer token for that case (and undefined otherwise, so the default token
// is used). Applied to lookup, read and write so all three see the account.
// `accountId` is a V3 id, or the V4 uuid itself when a V4-native session has no
// V3 id — only the former needs the migration lookup.
const resolveUuid = async (accountId, auth) => isV4Uuid(accountId)
	? accountId
	: getV4AccountUuid(accountId, auth);

const fetchAccount = async (accountId) => {
	const auth = v4AuthFor(accountId);
	const uuid = await resolveUuid(accountId, auth);
	const res = await v4api.accountsDetail(uuid, auth);
	return {uuid, account: res?.data || {}, auth};
};

// Every feature shares one account document and a write is a read-modify-write of
// all of it, so overlapping writers would drop each other's change.
const writeQueues = new Map();
const serialise = (uuid, task) => {
	const previous = writeQueues.get(uuid) || Promise.resolve();
	// A failed write must not poison the queue.
	const next = previous.catch(() => {}).then(task);
	writeQueues.set(uuid, next.catch(() => {}));
	return next;
};

const updateStorage = async (accountId, mutate) => {
	const auth = v4AuthFor(accountId);
	const uuid = await resolveUuid(accountId, auth);
	// The read belongs inside the critical section, or a queued writer works from
	// a snapshot taken before the previous PUT.
	return serialise(uuid, async () => {
		const [res, whitelabel] = await Promise.all([
			v4api.accountsDetail(uuid, auth),
			fetchStoredV4Whitelabel(uuid, auth),
		]);
		const account = res?.data || {};
		const storage = {...(account.storage || {})};
		mutate(storage);
		await v4api.accountsUpdate(uuid, {
			name: account.name,
			whitelabel,
			settings: {...(account.settings || {})},
			storage,
		}, auth);
	});
};

export class AccountStorageAPI {

	async getAll(accountId) {
		const {account} = await fetchAccount(accountId);
		return account.storage || {};
	}

	async setItem(accountId, key, name, value) {
		await this.setItems(accountId, [{key, name, value}]);
	}

	// One read-modify-write for every entry, rather than one per entry.
	async setItems(accountId, entries) {
		const list = (entries || []).filter(e => e && e.key);
		if (!list.length) return;
		await updateStorage(accountId, (storage) => {
			list.forEach(({key, name, value}) => {
				storage[key] = {
					name,
					content_type: 'application/json',
					content: encodeContent(value),
				};
			});
		});
	}

	async deleteItem(accountId, key) {
		await updateStorage(accountId, (storage) => {
			delete storage[key];
		});
	}

}

AccountStorageAPI.decodeContent = decodeContent;
AccountStorageAPI.encodeContent = encodeContent;
